import type { CartItem } from '../types/product';
import { 
  SHOPIFY_STOREFRONT_TOKEN, 
  SHOPIFY_API_VERSION,
  getStorefrontEndpoint 
} from '../config/shopify';

const CART_CREATE_MUTATION = `
  mutation cartCreate($input: CartInput!) {
    cartCreate(input: $input) {
      cart {
        id
        checkoutUrl
        totalQuantity
        cost {
          totalAmount {
            amount
            currencyCode
          }
        }
      }
      userErrors {
        field
        message
      }
    }
  }
`;

interface ShopifyCartLine {
  merchandiseId: string;
  quantity: number;
}

interface CheckoutResult {
  success: boolean;
  checkoutUrl?: string;
  error?: string;
}

/**
 * Converts a Shopify GID to the format needed for cart lines.
 * Storefront API variant IDs come as "gid://shopify/ProductVariant/12345"
 * which is exactly what cartCreate expects.
 */
function ensureGid(variantId: string): string {
  if (variantId.startsWith('gid://')) {
    return variantId;
  }
  // If it's just a numeric ID, wrap it
  return `gid://shopify/ProductVariant/${variantId}`;
}

/**
 * Validates if a variant ID is a valid Shopify GID or numeric ID
 */
function isValidShopifyVariantId(variantId: string): boolean {
  if (!variantId) return false;
  // Check if it's a Shopify GID format
  if (variantId.includes('gid://shopify/ProductVariant/')) return true;
  // Check if it's a numeric ID
  if (/^\d+$/.test(variantId)) return true;
  return false;
}

/**
 * Creates a Shopify cart from local cart items and returns the checkout URL.
 * This function is called ONLY when user clicks "Proceed to Checkout".
 * Local cart logic remains intact until this moment.
 */
export async function createShopifyCheckout(cartItems: CartItem[]): Promise<CheckoutResult> {
  // Validate cart is not empty
  if (!cartItems || cartItems.length === 0) {
    return { 
      success: false, 
      error: 'Your cart is empty. Please add items before checking out.' 
    };
  }

  // Filter to only items with valid Shopify variant IDs
  const validItems = cartItems.filter(item => {
    const variantId = item.selectedVariantId || '';
    return isValidShopifyVariantId(variantId);
  });

  // Check if we have any valid Shopify items
  if (validItems.length === 0) {
    return {
      success: false,
      error: 'Your cart contains items that cannot be checked out. Please refresh and try again.'
    };
  }

  // Warn if some items were filtered out
  if (validItems.length < cartItems.length) {
    console.warn(
      `Filtered out ${cartItems.length - validItems.length} invalid cart items during checkout`
    );
  }

  // Convert cart items to Shopify cart lines
  const lines: ShopifyCartLine[] = validItems.map(item => ({
    merchandiseId: ensureGid(item.selectedVariantId || ''),
    quantity: item.quantity
  }));

  try {
    // Use proxy in development, direct endpoint in production
    const endpoint = import.meta.env.DEV 
      ? `/shopify/api/${SHOPIFY_API_VERSION}/graphql.json`
      : getStorefrontEndpoint();

    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': SHOPIFY_STOREFRONT_TOKEN,
      },
      body: JSON.stringify({
        query: CART_CREATE_MUTATION,
        variables: {
          input: { lines }
        }
      }),
    });

    // Handle network errors
    if (!response.ok) {
      console.error('Shopify API HTTP error:', response.status, response.statusText);
      
      // Provide specific error messages based on status code
      if (response.status === 401 || response.status === 403) {
        return {
          success: false,
          error: 'Authentication error. Please contact support.'
        };
      }
      
      if (response.status >= 500) {
        return {
          success: false,
          error: 'Checkout service temporarily unavailable. Please try again in a moment.'
        };
      }
      
      return {
        success: false,
        error: 'Unable to connect to checkout. Please check your connection and try again.'
      };
    }

    const data = await response.json();

    // Handle GraphQL errors
    if (data.errors) {
      const errorMsg = data.errors.map((e: any) => e.message).join(', ');
      console.error('Shopify GraphQL errors:', data.errors);
      return {
        success: false,
        error: 'Checkout service error. Please try again or contact support.'
      };
    }

    const cartCreate = data.data?.cartCreate;

    // Handle user errors from Shopify
    if (cartCreate?.userErrors?.length > 0) {
      const errorMsg = cartCreate.userErrors
        .map((e: any) => e.message)
        .join(', ');
      console.error('Shopify user errors:', cartCreate.userErrors);
      
      // Provide user-friendly error messages
      if (errorMsg.toLowerCase().includes('inventory')) {
        return { 
          success: false, 
          error: 'Some items are no longer available. Please update your cart.' 
        };
      }
      
      if (errorMsg.toLowerCase().includes('variant')) {
        return { 
          success: false, 
          error: 'Invalid product selection. Please refresh and try again.' 
        };
      }
      
      return { 
        success: false, 
        error: errorMsg || 'Unable to create checkout. Please try again.' 
      };
    }

    const checkoutUrl = cartCreate?.cart?.checkoutUrl;

    // Validate we received a checkout URL
    if (!checkoutUrl) {
      console.error('No checkout URL returned from Shopify');
      return { 
        success: false, 
        error: 'Could not create checkout session. Please try again.' 
      };
    }

    return { success: true, checkoutUrl };
  } catch (error: any) {
    console.error('Shopify checkout error:', error);
    
    // Handle specific error types
    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      return {
        success: false,
        error: 'Network error. Please check your internet connection and try again.'
      };
    }
    
    if (error.name === 'AbortError') {
      return {
        success: false,
        error: 'Request timeout. Please try again.'
      };
    }
    
    return {
      success: false,
      error: 'An unexpected error occurred. Please try again.'
    };
  }
}

/**
 * Creates checkout and redirects the user to Shopify's hosted checkout page.
 * Uses window.location.href for clean, full-page redirect (no popups).
 * Returns error message if checkout fails, null if successful.
 * 
 * ✅ Headless-correct approach:
 * - Cart state stays in React until checkout
 * - Creates Shopify checkout via Storefront API
 * - Redirects using window.location.href (not window.open)
 * - No iframes, no embedded checkout
 */
export async function redirectToShopifyCheckout(cartItems: CartItem[]): Promise<string | null> {
  const result = await createShopifyCheckout(cartItems);

  if (result.success && result.checkoutUrl) {
    // Redirect to Shopify checkout using window.location.href
    // This is the correct headless approach - full page redirect
    window.location.href = result.checkoutUrl;
    return null;
  }

  // Return error message for display
  return result.error || 'Checkout failed. Please try again.';
}
